import { HttpClient } from '@wix/http-client';
import {
  ImageUploadHandlers,
  TEXT_ON_IMAGE_BRIGHTNESS,
  TEXT_ON_IMAGE_FLOWS,
  TextOnImageDefaultParams,
  TextOnImageGetPhotoSceneParams,
  TextOnImageObjectPhotoParams,
  TextOnImageResponse,
} from '../types';
import { getRandomTemplateImageBySectionAndFlow } from './fetchDataUtils';
import {
  applyColorOverlayAndGetFile,
  applyImageOverlayAndGetFile,
  getStaticImageUrl,
  reportTextOnImageError,
} from './utils';
import { PromptHubAcceptedUrls } from '../../chatController/sgChat/constants';
import {
  AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
  SG_FEATURE_NAME,
} from '../../consts';
import {
  FLUX_DEV_OBJECT_FLOW_FINAL_PROMPT_ID,
  FLUX_SCHNELL_OBJECT_PROMPT_ID,
  PHOTO_SCENES_PROMPT_IDS,
  RGB_PROMPT_IDS,
} from './consts';
import { getStaticUriFromBlob } from '../utils';

const CONTROL_IMAGE_URL =
  'https://static.wixstatic.com/media/47a484_10e614306c364326b30ea22c558a5319~mv2.jpg';

export const getObjectFlowImage = async (
  httpClient: HttpClient,
  sectionName: string,
  params: TextOnImageGetPhotoSceneParams,
  imageUploadHandlers: ImageUploadHandlers,
): Promise<TextOnImageResponse | undefined> => {
  if (
    !imageUploadHandlers.fileUploadFn ||
    !imageUploadHandlers.imageUrlUploadFn
  ) {
    return;
  }
  const { photoTheme, brightness, siteDescription, businessTerm, sectionId } =
    params;

  const photoScene = await getTextOnImagePhotoSceneObjectFlow(httpClient, {
    photoTheme,
    siteDescription,
    businessTerm,
    sectionId,
  });

  const getObjectImage = async () => {
    const schnellImageUrl = await getSchnellImageUrl(httpClient, photoScene);
    if (!schnellImageUrl) return;

    const uploadedImageUrl = await imageUploadHandlers.imageUrlUploadFn?.({
      url: schnellImageUrl,
      name: 'object.png',
    });
    if (!uploadedImageUrl) return;

    return uploadedImageUrl;
  };

  const templateImageFromCMS = await getRandomTemplateImageBySectionAndFlow(
    sectionName,
    TEXT_ON_IMAGE_FLOWS.OBJECT,
    sectionId,
  );

  const getBackgroundImage = async () => {
    const rgb = await getTextOnImageRgbObjectFlow(httpClient, {
      photoTheme,
      brightness,
    });

    const backgroundImage = await applyColorOverlayAndGetFile(
      templateImageFromCMS?.url || CONTROL_IMAGE_URL,
      rgb,
      brightness,
    );

    return backgroundImage || CONTROL_IMAGE_URL;
  };

  const objectImage = await getObjectImage();
  if (!objectImage) return;

  const backgroundImageUrl = await getBackgroundImage();

  const removeBgImageUrl = await getRemoveBgImage(httpClient, objectImage);
  if (!removeBgImageUrl) return;

  const imageOnImage = await applyImageOverlayAndGetFile(
    backgroundImageUrl,
    removeBgImageUrl,
    templateImageFromCMS?.dimensions,
  );
  if (!imageOnImage) return;

  const uploadedImageUrl = await getStaticUriFromBlob(
    imageOnImage,
    'objectFlowImage.png',
    imageUploadHandlers.fileUploadFn,
  );

  const seedImage = getStaticImageUrl(uploadedImageUrl);

  const imageUrl = await getFinalImage(httpClient, {
    brightness,
    seedImage,
    object_item1: photoScene,
  });

  return { imageUrl, photoScene, controlImageUrl: seedImage };
};

export const getTextOnImageRgbObjectFlow = async (
  httpClient: HttpClient,
  params: { photoTheme: string; brightness: TEXT_ON_IMAGE_BRIGHTNESS },
): Promise<string> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_URL,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptId: RGB_PROMPT_IDS[TEXT_ON_IMAGE_FLOWS.OBJECT],
        params,
        userRequestInfo: {
          featureName: SG_FEATURE_NAME,
        },
      },
    );
    const { RGB } = JSON.parse(data.response.generatedTexts[0]);
    return RGB;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(`Failed to get rgb: ${errorResponse}`);
    return '0,0,0';
  }
};

const getTextOnImagePhotoSceneObjectFlow = async (
  httpClient: HttpClient,
  params: TextOnImageDefaultParams,
): Promise<string> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_URL,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptId: PHOTO_SCENES_PROMPT_IDS[TEXT_ON_IMAGE_FLOWS.OBJECT],
        params,
        userRequestInfo: {
          featureName: SG_FEATURE_NAME,
        },
      },
    );
    const { object_item1 } = JSON.parse(data.response.generatedTexts[0]);
    return object_item1;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(
      `Failed to get object photo scene: ${errorResponse}`,
    );
    return 'A sleek, matte black stainless steel tumbler with a polished gold lid, featuring a minimalist design and vacuum insulation for temperature retention.';
  }
};

const getSchnellImageUrl = async (
  httpClient: HttpClient,
  object_item1: string,
): Promise<string | undefined> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_GET_RUNWARE_AI_IMAGE,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptIdToFetch: FLUX_SCHNELL_OBJECT_PROMPT_ID,
        params: { object_item1 },
      },
    );

    return data?.response?.url;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(`Failed to get object flow image: ${errorResponse}`);
  }
};

const getFinalImage = async (
  httpClient: HttpClient,
  params: TextOnImageObjectPhotoParams,
): Promise<string | undefined> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_GET_RUNWARE_AI_IMAGE,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        promptIdToFetch: FLUX_DEV_OBJECT_FLOW_FINAL_PROMPT_ID,
        params,
      },
    );

    return data?.response?.url;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(
      `Failed to get final object flow image: ${errorResponse}`,
    );
  }
};

export const getRemoveBgImage = async (
  httpClient: HttpClient,
  imageUrl: string,
): Promise<string | undefined> => {
  try {
    const { data } = await httpClient.post(
      PromptHubAcceptedUrls.AI_GATEWAY_GET_PHOTOROOM_AI_REMOVE_BG,
      {
        appDefId: AI_GATEWAY_APP_DEF_FOR_GET_PROMPTS,
        params: {
          imageUrl,
          additionalTags: 'SG',
          featureName: 'textOnImage',
        },
      },
    );
    return data?.response?.url;
  } catch (error: any) {
    const errorResponse = error.response?.data;
    reportTextOnImageError(
      `Failed to get removed BG object flow image: ${errorResponse}`,
    );
  }
};
